import axios, { AxiosError } from 'axios';
import { useRateLimitStore } from '@/stores/rateLimitStore';

// Use environment variable or default to direct API
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.blahblah.chat/api/v1';

// Custom error class for rate limiting
export class RateLimitError extends Error {
  retryAfter: number;

  constructor(message: string, retryAfter: number) {
    super(message);
    this.name = 'RateLimitError';
    this.retryAfter = retryAfter;
  }
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor - handle token refresh and rate limiting
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;

    // Handle rate limiting (429 Too Many Requests)
    if (error.response?.status === 429 && originalRequest) {
      const retryAfter = parseInt(error.response.headers['retry-after'] || '60', 10);
      const endpoint = originalRequest.url || '';

      // Update rate limit store
      useRateLimitStore.getState().handleRateLimitResponse(endpoint, retryAfter);

      // Return a specific error for rate limiting
      const errorData = error.response.data as { message?: string } | undefined;
      const message = errorData?.message || `Rate limited. Please wait ${retryAfter} seconds.`;
      return Promise.reject(new RateLimitError(message, retryAfter));
    }

    // Handle authentication errors
    if (error.response?.status === 401 && originalRequest) {
      const refreshToken = localStorage.getItem('refresh_token');

      if (refreshToken) {
        try {
          const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });

          localStorage.setItem('access_token', data.access_token);
          localStorage.setItem('refresh_token', data.refresh_token);

          // Retry original request
          originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
          return api(originalRequest);
        } catch {
          // Refresh failed - clear tokens and redirect to login
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
        }
      }
    }

    return Promise.reject(error);
  }
);

export default api;
